/* A stripped down polyfill of:
https://github.com/vercel/next.js/blob/09a2167b0a970757606b7f91ff2d470f77f13f8c/packages/next/src/shared/lib/router/router.ts
*/
import { useSyncExternalStore } from 'react';

type Query = Record<string, string | string[] | undefined>;
type ObjectURL = { pathname?: string; query?: Query };
type NextURL = string | ObjectURL;

// event name
const POP_STATE = 'popstate';

// history method names
const PUSH_STATE = 'pushState';
const REPLACE_STATE = 'replaceState';
type HistoryMethod = typeof PUSH_STATE | typeof REPLACE_STATE;
const HISTORY_METHODS = [PUSH_STATE, REPLACE_STATE] as const;

function parseQuery(search: string): Query {
  const params = new URLSearchParams(search);
  const out: Query = {};
  params.forEach((value, key) => {
    // collapse duplicate keys into arrays, like Next does
    if (key in out) {
      out[key] = Array.isArray(out[key])
        ? [...(out[key] as string[]), value]
        : [out[key] as string, value];
    } else {
      out[key] = value;
    }
  });
  return out;
}

function buildUrl(obj: ObjectURL) {
  const url = new URL(window.location.href);
  if (obj.pathname) url.pathname = obj.pathname;
  if (obj.query) {
    url.search = '';
    for (const [k, v] of Object.entries(obj.query)) {
      if (Array.isArray(v)) v.forEach(x => url.searchParams.append(k, x));
      else if (v !== undefined) url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

// Mock NextJS's event system
type Handler = (...args: any[]) => void;
class RouterEvents {
  private listeners: Record<string, Set<Handler>> = {};

  on(type: string, fn: Handler) {
    (this.listeners[type] ||= new Set()).add(fn);
  }
  off(type: string, fn: Handler) {
    this.listeners[type]?.delete(fn);
  }
  emit(type: string, ...args: any[]) {
    this.listeners[type]?.forEach(fn => fn(...args));
  }
}
const events = new RouterEvents();

// Subscribe to changes in the URL
const subscribers = new Set<() => void>();
const notify = () => subscribers.forEach(fn => fn());
function subscribe(cb: () => void) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

window.addEventListener(POP_STATE, () => {
  const url = window.location.href;
  events.emit('routeChangeStart', url);
  notify();
  events.emit('routeChangeComplete', url);
});

// monkey-patch history.pushState and history.replaceState so we get notified on programmatic nav
HISTORY_METHODS.forEach(m => {
  const orig = window.history[m];
  window.history[m] = function (state: any, _unused: '', url: string) {
    const originalResult = orig.apply(this, [state, _unused, url]);

    // This causes react-router to see the change.
    window.dispatchEvent(new PopStateEvent('popstate', { state }));

    return originalResult;
  };
});

type Snapshot = { pathname: string; asPath: string; query: Query };
let snapshot: Snapshot = { pathname: '', asPath: '', query: {} };
function getSnapshot() {
  const { pathname, search, hash } = window.location;
  const asPath = `${pathname}${search}${hash}`;
  if (asPath !== snapshot.asPath) {
    snapshot = {
      pathname,
      asPath,
      query: parseQuery(search),
    };
  }
  return snapshot;
}

const navigate = (method: HistoryMethod, to: NextURL): Promise<boolean> => {
  const url = typeof to === 'string' ? to : buildUrl(to);
  window.history[method]({}, '', url);
  return Promise.resolve(true);
};

const api = {
  push: (to: NextURL) => navigate(PUSH_STATE, to),
  replace: (to: NextURL) => navigate(REPLACE_STATE, to),
  back: () => window.history.back(),
  forward: () => window.history.forward(),
  reload: () => window.location.reload(),
  prefetch: async () => {},
};

export function useRouter() {
  const state = useSyncExternalStore(subscribe, getSnapshot);

  return {
    ...state,
    route: state.pathname,
    ...api,
    events,
  };
}

// keep default export for “import router from 'next/router'” compatibility
export default { useRouter, events, ...api };
